import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CortexDialog, CortexDialogBody, CortexDialogFooter } from '../components/dialogs/CortexDialog';
import { Save as SaveIcon } from 'lucide-react';
import { Button as UiButton, Chip, CoordioDropdown, FormField, Input, ShellHeader, Switch, Textarea, useStaticDropdownSource } from '@coordio/ui';
import { FormSection, PageContainer } from '@coordio/ui/layout';
import { AGENT_DROPDOWN, agentToDropdownOption } from '../components/agents/agentDropdown';
import { agentService } from '../services/agentService';
import { memoryModuleService } from '../services/memoryModuleService';
import { useNotificationStore } from '../store/notificationStore';
import type { AgentDefinitionDto } from '../types/agent';
import {
  SCOPE_DIMENSIONS,
  lifecycleStatusLabel,
  type AgentMemoryDefinitionDto,
  type MemoryDefinitionInput,
  type ScopeSlotDto,
} from '../types/memoryModule';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import {
  DEFAULT_EPISODE_SCHEMA_JSON,
  validateEpisodeSchemaJson,
} from '../utils/episodeSchemaValidation';
import { ROUTES } from '../utils/routes';

interface FormState {
  displayName: string;
  description: string;
  taskName: string;
  taskDescription: string;
  scopeSlots: ScopeSlotDto[];
  episodeSchemaJson: string;
  instructionsTokenBudget: number;
  episodicTokenBudget: number;
  minInjectionScore: number;
  candidateFetchLimit: number;
  hybridSearchEnabled: boolean;
  recencyHalfLifeDays: number;
  retrievalRefreshEveryNTurns: number;
  reflectionEnabled: boolean;
  owner: string;
  changeReason: string;
}

const EMPTY_SLOTS: ScopeSlotDto[] = [1, 2, 3, 4].map((ordinal) => ({
  ordinal,
  dimensionKey: null,
  required: false,
}));

const DEFAULT_FORM: FormState = {
  displayName: '',
  description: '',
  taskName: '',
  taskDescription: '',
  scopeSlots: EMPTY_SLOTS,
  episodeSchemaJson: DEFAULT_EPISODE_SCHEMA_JSON,
  instructionsTokenBudget: 1200,
  episodicTokenBudget: 2000,
  minInjectionScore: 0.35,
  candidateFetchLimit: 50,
  hybridSearchEnabled: true,
  recencyHalfLifeDays: 90,
  retrievalRefreshEveryNTurns: 6,
  reflectionEnabled: false,
  owner: '',
  changeReason: '',
};

const tryPretty = (json: string): string => {
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
};

const toForm = (d: AgentMemoryDefinitionDto): FormState => ({
  displayName: d.displayName,
  description: d.description ?? '',
  taskName: d.taskName,
  taskDescription: d.taskDescription,
  scopeSlots:
    d.scopeSlots.length > 0
      ? [1, 2, 3, 4].map(
          (ordinal) =>
            d.scopeSlots.find((s) => s.ordinal === ordinal) ?? {
              ordinal,
              dimensionKey: null,
              required: false,
            }
        )
      : EMPTY_SLOTS,
  episodeSchemaJson: tryPretty(d.episodeSchemaJson),
  instructionsTokenBudget: d.instructionsTokenBudget,
  episodicTokenBudget: d.episodicTokenBudget,
  minInjectionScore: d.minInjectionScore,
  candidateFetchLimit: d.candidateFetchLimit,
  hybridSearchEnabled: d.hybridSearchEnabled,
  recencyHalfLifeDays: d.recencyHalfLifeDays,
  retrievalRefreshEveryNTurns: d.retrievalRefreshEveryNTurns,
  reflectionEnabled: d.reflectionEnabled,
  owner: d.owner ?? '',
  changeReason: '',
});

const toInput = (form: FormState): MemoryDefinitionInput => ({
  displayName: form.displayName.trim(),
  description: form.description.trim() || null,
  taskName: form.taskName.trim(),
  taskDescription: form.taskDescription.trim(),
  scopeSlots: form.scopeSlots,
  episodeSchemaJson: form.episodeSchemaJson,
  instructionsTokenBudget: form.instructionsTokenBudget,
  episodicTokenBudget: form.episodicTokenBudget,
  minInjectionScore: form.minInjectionScore,
  candidateFetchLimit: form.candidateFetchLimit,
  hybridSearchEnabled: form.hybridSearchEnabled,
  recencyHalfLifeDays: form.recencyHalfLifeDays,
  retrievalRefreshEveryNTurns: form.retrievalRefreshEveryNTurns,
  reflectionEnabled: form.reflectionEnabled,
  owner: form.owner.trim() || null,
  changeReason: form.changeReason.trim() || null,
});

/**
 * Agent Memory Definition editor (Memory spec §2A.7, §9.9.3). Handles both creation
 * (/memory-definitions/new, with clone-from) and versioning of an existing definition —
 * a change is always a new version row, never an update in place.
 */
export const MemoryDefinitionEditorPage = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [head, setHead] = useState<AgentMemoryDefinitionDto | null>(null);
  const [versions, setVersions] = useState<AgentMemoryDefinitionDto[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [schemaErrors, setSchemaErrors] = useState<string[] | null>(null);

  // Create-mode state
  const [agents, setAgents] = useState<AgentDefinitionDto[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<AgentDefinitionDto | null>(null);
  const [cloneSources, setCloneSources] = useState<AgentMemoryDefinitionDto[]>([]);
  const [activate, setActivate] = useState(false);
  const [approvedBy, setApprovedBy] = useState('');
  const agentOptions = useMemo(() => agents.map(agentToDropdownOption), [agents]);
  const agentSource = useStaticDropdownSource(agentOptions);

  // Version-history viewer
  const [viewVersion, setViewVersion] = useState<AgentMemoryDefinitionDto | null>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    const load = async () => {
      try {
        if (isNew) {
          const [agentPage, defs] = await Promise.all([
            agentService.getAgents({ pageSize: 200, isActive: true }),
            memoryModuleService.listDefinitions(),
          ]);
          setAgents(agentPage.items);
          setCloneSources(defs);
        } else {
          const detail = await memoryModuleService.getDefinition(id!);
          setHead(detail.definition);
          setVersions(detail.versions);
          setForm(toForm(detail.definition));
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const configuredDimensions = useMemo(
    () => form.scopeSlots.filter((s) => s.dimensionKey).map((s) => s.dimensionKey!),
    [form.scopeSlots]
  );

  const runSchemaValidation = (): boolean => {
    const errors = validateEpisodeSchemaJson(form.episodeSchemaJson, configuredDimensions);
    setSchemaErrors(errors);
    return errors.length === 0;
  };

  const handleCloneFrom = async (source: AgentMemoryDefinitionDto | null) => {
    if (!source) return;
    try {
      const detail = await memoryModuleService.getDefinition(source.agentMemoryDefinitionId);
      const cloned = toForm(detail.definition);
      setForm({ ...cloned, changeReason: `Cloned from ${detail.definition.displayName}` });
      addNotification(`Cloned configuration from "${detail.definition.displayName}"`, 'info');
    } catch {
      addNotification('Failed to load clone source', 'error');
    }
  };

  const handleSave = async () => {
    if (!form.displayName.trim() || !form.taskName.trim() || !form.taskDescription.trim()) {
      addNotification('Display name, task name and task description are required', 'error');
      return;
    }
    if (isNew && !selectedAgent) {
      addNotification('Select the platform agent this definition applies to', 'error');
      return;
    }
    if (!runSchemaValidation()) {
      addNotification('Fix the episode schema violations before saving', 'error');
      return;
    }
    if (activate && !approvedBy.trim()) {
      addNotification('Activation requires an approver', 'error');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        const created = await memoryModuleService.createDefinition({
          agentDefinitionId: selectedAgent!.id,
          definition: toInput(form),
          activate,
          approvedBy: activate ? approvedBy.trim() : null,
        });
        addNotification(`Definition created (v${created.version})`, 'success');
        navigate(`/memory-definitions/${created.agentMemoryDefinitionId}`);
      } else {
        const created = await memoryModuleService.createDefinitionVersion(id!, {
          definition: toInput(form),
          activate,
          approvedBy: activate ? approvedBy.trim() : null,
        });
        addNotification(`Version ${created.version} created`, 'success');
        const detail = await memoryModuleService.getDefinition(id!);
        setHead(detail.definition);
        setVersions(detail.versions);
        setForm(toForm(detail.definition));
      }
    } catch (err: any) {
      const data = err.response?.data;
      const details = Array.isArray(data?.errors) ? `: ${data.errors.join('; ')}` : '';
      addNotification((data?.message || 'Save failed') + details, 'error');
    } finally {
      setSaving(false);
    }
  };

  const versionColumns: DataTableColumn<AgentMemoryDefinitionDto>[] = [
    { id: 'version', label: 'Version', minWidth: 80, render: (v) => `v${v.version}` },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      render: (v) => (
        <Chip
          variant={v.isCurrent ? 'filled' : 'outlined'}
          label={lifecycleStatusLabel(v.status)}
        />
      ),
    },
    { id: 'changeReason', label: 'Change reason', minWidth: 220, render: (v) => v.changeReason || '—' },
    { id: 'createdBy', label: 'Created by', minWidth: 140, render: (v) => v.createdBy },
    {
      id: 'createdAtUtc',
      label: 'Created',
      minWidth: 150,
      render: (v) => new Date(v.createdAtUtc).toLocaleString(),
    },
    { id: 'approvedBy', label: 'Approved by', minWidth: 140, render: (v) => v.approvedBy || '—' },
    {
      id: 'actions',
      label: '',
      minWidth: 80,
      align: 'right',
      render: (v) => (
        <button className="text-sm text-[var(--color-accent-primary)] hover:underline" onClick={() => setViewVersion(v)}>
          View
        </button>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="memory-definition-editor" data-testid="memory-definition-form-page">
        <ShellHeader title="Memory Definition" description="Loading…" data-testid="memory-definition-header" />
        <PageContainer>
          <p className="text-[var(--color-text-secondary)]">Loading…</p>
        </PageContainer>
      </div>
    );
  }

  return (
    <div className="memory-definition-editor" data-testid="memory-definition-form-page">
      <ShellHeader
        title={isNew ? 'New Memory Definition' : head?.displayName || 'Memory Definition'}
        data-testid="memory-definition-header"
        description={
          !isNew && head
            ? `Agent ${head.agentKey} · current v${head.version} · ${lifecycleStatusLabel(head.status)}. Saving creates version ${head.version + 1}.`
            : isNew
              ? 'Create a new memory definition for an agent.'
              : undefined
        }
        actions={
          <div className="flex items-center gap-1">
            <button
              className="rounded-lg bg-[var(--color-accent-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
              onClick={handleSave}
              disabled={saving}
              data-testid="save-memory-definition-btn"
            >
              <SaveIcon size={16} />
              {isNew ? 'Create' : 'Save as New Version'}
            </button>
            <UiButton type="button" variant="outline" size="sm" onClick={() => navigate(ROUTES.MEMORY_DEFINITIONS)} data-testid="back-memory-definition-btn">
              Back
            </UiButton>
          </div>
        }
      />

      <PageContainer>
      <div className="flex flex-col gap-5">

      {error && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm mb-4 flex items-center justify-between" data-testid="memory-definition-error-panel">
          <span>{error}</span>
          <button className="text-xs underline ml-2" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {isNew && (
        <FormSection title="Target agent" description="Select the platform agent and optionally clone from an existing definition." data-testid="memory-definition-section-target-agent">
          <div className="grid gap-5 items-end sm:grid-cols-2">
            <FormField label="Platform agent" required description="The agent this memory definition applies to." data-testid="memory-definition-platformAgent">
              <CoordioDropdown
                source={agentSource}
                value={selectedAgent?.id ?? null}
                onChange={(val) => setSelectedAgent(agents.find((a) => a.id === val) ?? null)}
                {...AGENT_DROPDOWN}
                clearable={false}
                placeholder="Select platform agent"
                data-testid="memory-definition-platformAgent"
                data-pw="platformAgentId"
                data-field-type="select"
              />
            </FormField>
            <FormField label="Clone from existing definition" description="Optionally clone configuration from an existing definition." data-testid="memory-definition-cloneSource">
              <CoordioDropdown
                options={cloneSources.map((d) => ({
                  value: d.agentMemoryDefinitionId,
                  label: `${d.displayName} (v${d.version})`,
                }))}
                value={null}
                onChange={(val) => {
                  const source = cloneSources.find((d) => d.agentMemoryDefinitionId === val) ?? null;
                  void handleCloneFrom(source);
                }}
                placeholder="Clone from…"
                searchPlaceholder="Search definitions…"
                clearable
                data-testid="memory-definition-cloneSource"
              />
            </FormField>
          </div>
        </FormSection>
      )}

      <FormSection title="Identity" description="Core fields that identify this memory definition." data-testid="memory-definition-section-identity">
        <div className="grid gap-5 items-end sm:grid-cols-2">
          <FormField label="Display name" required description="Human-readable name for this definition." data-testid="memory-definition-displayName">
            <Input
              className="w-full"
              required
              value={form.displayName}
              onChange={(e) => set('displayName', e.target.value)}
              data-testid="memory-definition-displayName"
            />
          </FormField>
          <FormField label="Owner" description="Accountable operations owner." data-testid="memory-definition-owner">
            <Input
              className="w-full"
              value={form.owner}
              onChange={(e) => set('owner', e.target.value)}
              data-testid="memory-definition-owner"
            />
          </FormField>
        </div>
        <div className="mt-5">
          <FormField label="Description" description="Optional context about this memory definition." data-testid="memory-definition-description">
            <Textarea
              className="w-full"
              rows={2}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              data-testid="memory-definition-description"
            />
          </FormField>
        </div>
        <div className="mt-5 grid gap-5 items-end sm:grid-cols-2">
          <FormField label="Task name" required description="Stable task identity used in retrieval." data-testid="memory-definition-taskName">
            <Input
              className="w-full"
              required
              value={form.taskName}
              onChange={(e) => set('taskName', e.target.value)}
              data-testid="memory-definition-taskName"
            />
          </FormField>
          <FormField label="Task description" required description="Describes the task for context in retrieval." data-testid="memory-definition-taskDescription">
            <Input
              className="w-full"
              required
              value={form.taskDescription}
              onChange={(e) => set('taskDescription', e.target.value)}
              data-testid="memory-definition-taskDescription"
            />
          </FormField>
        </div>
      </FormSection>

      <FormSection
        title="Scope slots"
        description="Up to four configurable dimensions (slot 5 is always UserId and cannot be configured). Required slots fail closed: a session missing a required dimension gets no memory."
        data-testid="memory-definition-section-scope-slots"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {form.scopeSlots.map((slot, i) => (
            <div key={slot.ordinal}>
              <FormField label={`Slot ${slot.ordinal}`} description={`Scope dimension for slot ${slot.ordinal}.`} data-testid={`memory-definition-scopeSlot-${slot.ordinal}`}>
                <CoordioDropdown
                  options={[
                    { value: '', label: '(unused)' },
                    ...SCOPE_DIMENSIONS.map((d) => ({
                      value: d.key,
                      label: d.key,
                      disabled: form.scopeSlots.some((s, j) => j !== i && s.dimensionKey === d.key),
                    })),
                  ]}
                  value={slot.dimensionKey ?? ''}
                  onChange={(val) => {
                    const next = [...form.scopeSlots];
                    next[i] = { ...slot, dimensionKey: val || null, required: val ? slot.required : false };
                    set('scopeSlots', next);
                  }}
                  data-testid={`memory-definition-scopeSlot-${slot.ordinal}`}
                />
              </FormField>
              <div className="mt-2 ui-form-choice-field">
                <div className="ui-form-choice-field__row">
                  <div className="ui-form-choice-field__control">
                    <Switch
                      checked={slot.required}
                      disabled={!slot.dimensionKey}
                      onCheckedChange={(checked) => {
                        const next = [...form.scopeSlots];
                        next[i] = { ...slot, required: checked };
                        set('scopeSlots', next);
                      }}
                    />
                  </div>
                  <div className="ui-form-choice-field__label-cell">
                    <span className="ui-form-choice-field__label">Required</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </FormSection>

      <FormSection
        title="Episode schema"
        description="The extraction contract for this agent's episodes (spec §9.9). Validated against the nine schema rules on save; at least one discriminating field must come from the tool trace."
        data-testid="memory-definition-section-episode-schema"
      >
        <div className="flex justify-end gap-1 mb-2">
          <button className="text-sm text-[var(--color-accent-primary)] hover:underline" onClick={() => set('episodeSchemaJson', DEFAULT_EPISODE_SCHEMA_JSON)} data-testid="memory-definition-reset-schema-btn">
            Reset to default
          </button>
          <button className="rounded-lg border border-[var(--color-border-default)] px-3 py-1 text-sm hover:bg-[var(--color-bg-subtle)]" onClick={runSchemaValidation} data-testid="memory-definition-validate-btn">
            Validate
          </button>
        </div>
        <FormField label="Schema JSON" description="JSON schema defining the episode extraction contract." data-testid="memory-definition-episodeSchemaJson">
          <Textarea
            className="w-full font-mono text-[13px]"
            rows={12}
            value={form.episodeSchemaJson}
            onChange={(e) => {
              set('episodeSchemaJson', e.target.value);
              setSchemaErrors(null);
            }}
            data-testid="memory-definition-episodeSchemaJson"
          />
        </FormField>
        {schemaErrors !== null && (
          schemaErrors.length === 0 ? (
            <div className="rounded-lg border border-green-500/40 bg-green-500/10 p-3 text-sm mt-2">Schema passes all client-side rules.</div>
          ) : (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm mt-2">
              <ul className="m-0 pl-5">
                {schemaErrors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )
        )}
      </FormSection>

      <FormSection title="Budgets and retrieval" description="Token budgets, scoring thresholds, and retrieval configuration." data-testid="memory-definition-section-budgets">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          <FormField label="Instructions token budget" description="Max tokens allocated for instructions." data-testid="memory-definition-instructionsTokenBudget">
            <Input
              className="w-full"
              type="number"
              value={form.instructionsTokenBudget}
              onChange={(e) => set('instructionsTokenBudget', Number(e.target.value))}
              data-testid="memory-definition-instructionsTokenBudget"
            />
          </FormField>
          <FormField label="Episodic token budget" description="Max tokens allocated for episodic memory." data-testid="memory-definition-episodicTokenBudget">
            <Input
              className="w-full"
              type="number"
              value={form.episodicTokenBudget}
              onChange={(e) => set('episodicTokenBudget', Number(e.target.value))}
              data-testid="memory-definition-episodicTokenBudget"
            />
          </FormField>
          <FormField label="Min injection score" description="Minimum relevance score for injection (0–1)." data-testid="memory-definition-minInjectionScore">
            <Input
              className="w-full"
              type="number"
              value={form.minInjectionScore}
              onChange={(e) => set('minInjectionScore', Number(e.target.value))}
              step={0.05}
              min={0}
              max={1}
              data-testid="memory-definition-minInjectionScore"
            />
          </FormField>
          <FormField label="Candidate fetch limit" description="Max candidates fetched per retrieval cycle." data-testid="memory-definition-candidateFetchLimit">
            <Input
              className="w-full"
              type="number"
              value={form.candidateFetchLimit}
              onChange={(e) => set('candidateFetchLimit', Number(e.target.value))}
              data-testid="memory-definition-candidateFetchLimit"
            />
          </FormField>
          <FormField label="Recency half-life (days)" description="Decay half-life for recency weighting." data-testid="memory-definition-recencyHalfLifeDays">
            <Input
              className="w-full"
              type="number"
              value={form.recencyHalfLifeDays}
              onChange={(e) => set('recencyHalfLifeDays', Number(e.target.value))}
              data-testid="memory-definition-recencyHalfLifeDays"
            />
          </FormField>
          <FormField label="Refresh every N turns" description="Re-retrieve memory every N conversation turns." data-testid="memory-definition-retrievalRefreshEveryNTurns">
            <Input
              className="w-full"
              type="number"
              value={form.retrievalRefreshEveryNTurns}
              onChange={(e) => set('retrievalRefreshEveryNTurns', Number(e.target.value))}
              data-testid="memory-definition-retrievalRefreshEveryNTurns"
            />
          </FormField>
          <div className="flex items-end">
            <div className="ui-form-choice-field">
              <div className="ui-form-choice-field__row">
                <div className="ui-form-choice-field__control">
                  <Switch
                    checked={form.hybridSearchEnabled}
                    onCheckedChange={(checked) => set('hybridSearchEnabled', checked)}
                    data-testid="memory-definition-hybridSearchEnabled"
                  />
                </div>
                <div className="ui-form-choice-field__label-cell">
                  <span className="ui-form-choice-field__label">Hybrid search</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-end">
            <div className="ui-form-choice-field">
              <div className="ui-form-choice-field__row">
                <div className="ui-form-choice-field__control">
                  <Switch
                    checked={form.reflectionEnabled}
                    onCheckedChange={(checked) => set('reflectionEnabled', checked)}
                    data-testid="memory-definition-reflectionEnabled"
                  />
                </div>
                <div className="ui-form-choice-field__label-cell">
                  <span className="ui-form-choice-field__label">Reflection (Phase 5)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </FormSection>

      <FormSection title="Publication" description="Version tracking, activation, and approval settings." data-testid="memory-definition-section-publication">
        <div className="grid gap-5 items-end sm:grid-cols-2">
          <FormField label="Change reason" description="Recorded on the version row." data-testid="memory-definition-changeReason">
            <Input
              className="w-full"
              value={form.changeReason}
              onChange={(e) => set('changeReason', e.target.value)}
              data-testid="memory-definition-changeReason"
            />
          </FormField>
          <div className="flex items-end">
            <div className="ui-form-choice-field">
              <div className="ui-form-choice-field__row">
                <div className="ui-form-choice-field__control">
                  <Switch
                    checked={activate}
                    onCheckedChange={(checked) => setActivate(checked)}
                    data-testid="memory-definition-activate"
                  />
                </div>
                <div className="ui-form-choice-field__label-cell">
                  <span className="ui-form-choice-field__label">Activate on save</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {activate && (
          <div className="mt-5">
            <FormField label="Approved by" required description="Activation requires an approver." data-testid="memory-definition-approvedBy">
              <Input
                className="w-full"
                required
                value={approvedBy}
                onChange={(e) => setApprovedBy(e.target.value)}
                data-testid="memory-definition-approvedBy"
              />
            </FormField>
          </div>
        )}
        {!activate && (
          <p className="text-sm text-[var(--color-text-secondary)] mt-2">
            Saved as Draft — the agent keeps its current behaviour until a version is activated.
          </p>
        )}
      </FormSection>

      {!isNew && versions.length > 0 && (
        <FormSection title="Version history" description="All previous versions of this definition." data-testid="memory-definition-section-version-history">
          <DataTable columns={versionColumns} rows={versions} size="small" />
        </FormSection>
      )}

      </div>
      </PageContainer>

      <CortexDialog
        open={viewVersion !== null}
        onClose={() => setViewVersion(null)}
        title={
          viewVersion
            ? `${viewVersion.displayName} — v${viewVersion.version} (${lifecycleStatusLabel(viewVersion.status)})`
            : undefined
        }
        size="sm"
        data-testid="memory-definition-version-dialog"
      >
        <CortexDialogBody>
          {viewVersion && (
            <div className="grid gap-2">
              <p className="text-sm">
                <strong>Task:</strong> {viewVersion.taskName} — {viewVersion.taskDescription}
              </p>
              <p className="text-sm">
                <strong>Scope:</strong>{' '}
                {viewVersion.scopeSlots.filter((s) => s.dimensionKey).map((s) => `${s.dimensionKey}${s.required ? ' (required)' : ''}`).join(', ') || 'none'}
              </p>
              <p className="text-sm">
                <strong>Budgets:</strong> instructions {viewVersion.instructionsTokenBudget},
                episodic {viewVersion.episodicTokenBudget}, min score {viewVersion.minInjectionScore},
                fetch limit {viewVersion.candidateFetchLimit}
              </p>
              <p className="text-sm">
                <strong>Change reason:</strong> {viewVersion.changeReason || '—'}
              </p>
              <div className="text-sm">
                <strong>Episode schema:</strong>
                <pre
                  className="mt-1 p-2 rounded overflow-auto max-h-80 text-xs border border-[var(--glass-border)]"
                >
                  {tryPretty(viewVersion.episodeSchemaJson)}
                </pre>
              </div>
              <button
                className="text-sm text-[var(--color-accent-primary)] hover:underline justify-self-start"
                onClick={() => {
                  setForm(toForm(viewVersion));
                  setViewVersion(null);
                  addNotification(`Editor loaded from v${viewVersion.version} — save to create a new version`, 'info');
                }}
              >
                Load into editor
              </button>
            </div>
          )}
        </CortexDialogBody>
        <CortexDialogFooter>
          <button className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-[var(--color-bg-subtle)]" onClick={() => setViewVersion(null)}>Close</button>
        </CortexDialogFooter>
      </CortexDialog>
    </div>
  );
};

export default MemoryDefinitionEditorPage;
